/**
 * What's in Pro — /whats-included (sidebar, next to Settings / Contact Us).
 * One clear answer to "what did I get?": every Pro feature (admin list from Admin → Pricing → Pro Features),
 * the plan status, and the one thing that IS counted — teacher-feedback credits (welcome amount, costs, balance).
 * All text: Admin → Pricing Page → "Student page text — What's in Pro".
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, Coins, Infinity as InfinityIcon, ArrowRight, Calendar, FileText, MessageSquareText, GraduationCap, Sparkles } from 'lucide-react';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { fill, usePageCopy } from '../lib/pageCopy';
import { IncludedList } from '../components/pro/IncludedList';

const STAFF = ['admin', 'teacher', 'contributor', 'sales'];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : '');

function PlanStrip({ c, isPro, sub, onUpgrade, pricing }) {
  const price = pricing?.price ? `${pricing.currency_symbol || 'CA$'}${pricing.price}` : '';
  return (
    <div className={`rounded-2xl border p-4 md:p-5 flex items-center gap-4 flex-wrap ${isPro ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50'}`} data-testid="plan-strip">
      <div className={`w-12 h-12 rounded-2xl text-white flex items-center justify-center shrink-0 ${isPro ? 'bg-emerald-600' : 'bg-amber-500'}`}><Crown size={22} /></div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-[Manrope] font-extrabold text-slate-900 text-lg" data-testid="plan-strip-title">{isPro ? c.plan_title_pro : c.plan_title_free}</p>
        <p className="text-sm text-slate-600" data-testid="plan-strip-sub">
          {isPro ? (sub?.expires_at ? fill(c.plan_sub_pro, { expires: fmtDate(sub.expires_at), days: sub.days_remaining ?? '' }) : 'Everything below is unlocked.')
            : fill(c.plan_sub_free, { period: (pricing?.period || '3 months').replace(/^for\s+/i, '') })}
        </p>
      </div>
      {!isPro && <button type="button" onClick={onUpgrade} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-6 py-2.5 shadow-lg shadow-amber-500/25" data-testid="plan-strip-upgrade">{fill(c.plan_upgrade, { price }).replace(/\s·\s*$/, '')}</button>}
    </div>
  );
}

function CreditsCard({ c, isPro, w, navigate }) {
  const costs = w?.costs || {};
  const offered = w?.offered || {};
  const welcome = w?.welcome_credits ?? 25;
  const sectionOn = ['reading', 'listening', 'writing', 'speaking'].some((k) => offered[k] !== false);
  const rows = [
    sectionOn && [c.cost_section, costs.writing ?? 25],
    offered.full_mock !== false && [c.cost_full, costs.full_mock ?? 100],
    offered.call_1on1 !== false && [c.cost_call, costs.call_1on1 ?? 25],
  ].filter(Boolean);
  return (
    <div className="rounded-3xl border border-teal-200 bg-white p-5 md:p-6" data-testid="credits-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-2xl bg-teal-700 text-white flex items-center justify-center shrink-0"><Coins size={20} /></div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-teal-700">{c.credits_kicker}</p>
          <h2 className="font-[Manrope] text-lg font-extrabold text-slate-900">{c.credits_title}</h2>
          <p className="text-sm text-slate-600 mt-1">{fill(c.credits_text, { welcome })}</p>
        </div>
        {isPro && (
          <div className="rounded-2xl bg-teal-50 border border-teal-100 px-4 py-3 text-center" data-testid="credits-balance">
            <p className="text-[11px] font-bold uppercase tracking-wide text-teal-700">Your balance</p>
            <p className="font-[Manrope] text-3xl font-extrabold text-teal-800 leading-none mt-1">{w?.balance ?? 0}</p>
            <p className="text-[11px] text-slate-500 mt-1">credits</p>
          </div>
        )}
      </div>
      <div className={`mt-4 grid grid-cols-1 gap-2 ${rows.length === 3 ? 'md:grid-cols-3' : rows.length === 2 ? 'md:grid-cols-2' : ''}`} data-testid="credits-costs">
        {rows.map(([label, cost]) => (
          <div key={label} className="rounded-xl border border-slate-200 px-3.5 py-3 flex items-center justify-between gap-3">
            <span className="text-sm text-slate-700">{label}</span>
            <span className="font-extrabold text-teal-800 whitespace-nowrap">{cost} cr</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {isPro ? (
          <>
            <button type="button" onClick={() => navigate('/feedback-credits')} className="inline-flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-bold px-5 py-2.5" data-testid="credits-use-btn"><MessageSquareText size={15} /> {c.credits_use_btn}</button>
            {offered.call_1on1 !== false && <button type="button" onClick={() => navigate('/feedback-credits?tab=calls')} className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-5 py-2.5 hover:border-slate-400" data-testid="credits-calls-btn">{c.credits_calls_btn}</button>}
          </>
        ) : <button type="button" onClick={() => goPro(navigate)} className="inline-flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-bold px-5 py-2.5" data-testid="credits-upgrade-btn"><Crown size={15} /> {fill(c.credits_upgrade_btn, { welcome })}</button>}
      </div>
    </div>
  );
}

const QUICK = [
  ['/group-classes', Calendar, 'Join a live class', 'Two live classes every day'],
  ['/exams', FileText, 'Start a mock exam', 'Full-length, real-test layout'],
  ['/feedback-credits', MessageSquareText, 'Ask a teacher', 'Video feedback with credits'],
  ['/courses', GraduationCap, 'Watch the course', 'CLB 9+ strategy videos'],
];

export default function WhatsIncluded() {
  const copy = usePageCopy('whats_included');
  const { trialMode, entryLabel } = useTrial();
  // Trial mode: the entry tier is called "Trial", not "Free" (Admin → Free vs Trial).
  const swap = (t) => (trialMode ? String(t || '').replace(/\bFree plan\b/g, `${entryLabel} plan`).replace(/\bFree\b/g, entryLabel) : t);
  const c = trialMode ? { ...copy, plan_title_free: swap(copy.plan_title_free), plan_sub_free: swap(copy.plan_sub_free), included_locked: swap(copy.included_locked) } : copy;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [sub, setSub] = useState(null);
  const [w, setW] = useState(null);
  const isPro = !!(user?.subscription_status && user.subscription_status !== 'free') || STAFF.includes(user?.role);
  useEffect(() => {
    api.get('/api/pricing/config').then((r) => setPricing(r.data || {})).catch(() => setPricing({}));
    api.get('/api/feedback-credits/me').then((r) => setW(r.data)).catch(() => setW({}));
    if (isPro) api.get('/api/subscription/status').then((r) => setSub(r.data)).catch(() => {});
  }, [isPro]);
  const features = pricing?.features?.length ? pricing.features : [];
  return (
    <DashboardLayout title={c.title}>
      <div className="space-y-5 -mt-2 max-w-4xl" data-testid="whats-included-page">
        <PlanStrip c={c} isPro={isPro} sub={sub} pricing={pricing} onUpgrade={() => goPro(navigate)} />

        <div className="rounded-3xl bg-[#0B1120] text-white p-6 md:p-8 relative overflow-hidden" data-testid="included-box">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="flex items-center justify-between gap-3 flex-wrap relative">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">{c.included_kicker}</p>
              <h2 className="font-[Manrope] text-xl md:text-2xl font-extrabold mt-1">{c.included_title}</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold px-3 py-1.5" data-testid="included-unlimited-badge"><InfinityIcon size={14} /> {c.included_badge}</span>
          </div>
          <div className="mt-5 relative">
            {!pricing ? <p className="text-white/50 text-sm">Loading…</p> : features.length ? <IncludedList features={features} isPro={isPro} dark /> : <p className="text-white/60 text-sm">Your admin has not listed the Pro features yet (Admin → Pricing → Pro Features).</p>}
          </div>
          {!isPro && <p className="mt-5 text-xs text-white/60 relative">{c.included_locked} <button type="button" onClick={() => goPro(navigate)} className="font-bold text-emerald-300 hover:text-emerald-200 underline" data-testid="included-box-upgrade">{c.included_locked_link}</button>.</p>}
        </div>

        <CreditsCard c={c} isPro={isPro} w={w} navigate={navigate} />

        {isPro && (
          <div data-testid="quick-links">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">{c.quick_title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {QUICK.map(([to, Icon, title, sub2]) => (
                <Link key={to} to={to} className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all" data-testid={`quick-${to.replace(/\W+/g, '')}`}>
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Icon size={17} /></div>
                  <p className="font-semibold text-slate-900 text-sm mt-3 inline-flex items-center gap-1">{title} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" /></p>
                  <p className="text-xs text-slate-500 mt-0.5">{sub2}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Sparkles size={12} /> {c.footer} <Link to="/contact" className="underline hover:text-slate-700" data-testid="included-contact-link">Contact us</Link> · <Link to="/settings" className="underline hover:text-slate-700">Settings</Link></p>
      </div>
    </DashboardLayout>
  );
}
